
import React from "react";
import { CornerDownLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useWordInputEffects } from "@/hooks/useWordInputEffects";
import { WordErrorDisplay } from "@/components/WordErrorDisplay";
import { SubmittedWordDisplay } from "@/components/SubmittedWordDisplay";

interface UserWordInputProps {
  word: string;
  isCurrentRound: boolean;
  isSubmitting: boolean;
  autoFocus: boolean;
  botIsThinking?: boolean;
  userHasSubmitted?: boolean;
  wordError?: string;
  onWordChange?: (word: string) => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onClearWordError?: () => void;
}

export const UserWordInput: React.FC<UserWordInputProps> = ({
  word,
  isCurrentRound,
  isSubmitting,
  autoFocus,
  botIsThinking = false,
  userHasSubmitted = false,
  wordError = "",
  onWordChange,
  onKeyPress,
  onClearWordError
}) => {
  const {
    inputRef,
    showCheckmark,
    showError,
    localErrorMessage,
    isErrorProcessing
  } = useWordInputEffects({
    isCurrentRound,
    autoFocus,
    userHasSubmitted,
    botIsThinking,
    wordError,
    onWordChange,
    onClearWordError
  });

  // Handle input changes and clear errors
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("Input changed, clearing any existing errors");
    // Always clear any error state when user types
    
    // Call parent's onClearWordError if there was an error
    if (wordError && onClearWordError) {
      onClearWordError();
    }
    
    // Update the word
    if (onWordChange) {
      onWordChange(e.target.value);
    }
  };

  if (!isCurrentRound) {
    return (
      <div className="text-foreground w-full text-center">
        {word.toLowerCase() || "..."}
      </div>
    );
  }

  // If we're showing an error message
  if (showError && localErrorMessage) {
    return <WordErrorDisplay errorMessage={localErrorMessage} />;
  }

  // If the user has submitted their word, show it as text or a checkmark
  if (userHasSubmitted) {
    return <SubmittedWordDisplay 
      word={word} 
      showCheckmark={false} 
      userHasSubmitted={userHasSubmitted} 
      botHasChosen={!botIsThinking} 
    />;
  }

  // CRITICAL FIX: Never disable the input, EVER - allow submission at all times
  const shouldDisableInput = false; // Changed from: isSubmitting && !botIsThinking;

  return (
    <div className="w-full relative">
      <Input 
        ref={inputRef} 
        type="text" 
        value={word} 
        onChange={handleInputChange} 
        onKeyDown={onKeyPress} 
        placeholder="choose a word..." 
        className="flex-1 bg-transparent border-0 shadow-none focus-visible:ring-0 focus-visible:outline-none p-0 h-8 text-center text-base -mt-1 pointer-events-auto" 
        disabled={shouldDisableInput} 
        style={{
          fontSize: "16px",
          lineHeight: "normal",
          pointerEvents: "auto",
          opacity: shouldDisableInput ? 0.5 : 1,
          zIndex: 10
        }}
        aria-disabled={shouldDisableInput}
        tabIndex={0}
        autoFocus={autoFocus}
      />
      {word.trim().length > 0 && !showError && (
        <div className="absolute bottom-0 right-0">
          <CornerDownLeft className="w-3.5 h-3.5 text-muted-foreground" />
        </div>
      )}
    </div>
  );
};
